'use client'

import Link from 'next/link'
import { Badge } from '@/components/ui/badge'
import type { QuoteCard } from '@/types/dashboard'

interface RecentQuotesProps {
  quotes: QuoteCard[]
}

export function RecentQuotes({ quotes }: RecentQuotesProps) {
  if (quotes.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 py-10 text-center">
        <p className="text-sm font-medium text-gray-600">최근 견적이 없습니다.</p>
        <p className="mt-1 text-xs text-gray-500">견적이 도착하면 여기에서 바로 확인할 수 있어요.</p>
      </div>
    )
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const StatusBadge = ({ status, label }: { status?: string; label?: string }) => {
    if (!label) return null
    const s = (status ?? '').toLowerCase()

    let cls = 'border-gray-200 bg-gray-50 text-gray-700'
    if (s.includes('pending') || s.includes('review')) cls = 'border-blue-200 bg-blue-50 text-blue-700'
    else if (s.includes('accepted') || s.includes('selected') || s.includes('success'))
      cls = 'border-emerald-200 bg-emerald-50 text-emerald-700'
    else if (s.includes('rejected') || s.includes('failed') || s.includes('cancel'))
      cls = 'border-red-200 bg-red-50 text-red-700'

    return (
      <Badge variant="outline" className={`text-xs font-semibold ${cls}`}>
        {label}
      </Badge>
    )
  }

  return (
    <div className="space-y-2">
      {quotes.map((quote) => {
        const title = (quote.title ?? '').trim() || '프로젝트 견적'

        return (
          <Link
            key={quote.id}
            href={quote.href}
            className="group block rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
          >
            {/* 상단: 제목(단독 줄, 2줄 허용) */}
            <p className="text-sm font-bold text-gray-900 group-hover:text-blue-700 line-clamp-2">
              {title}
            </p>

            {/* 중단: 배지들 (제목과 분리해서 눌리지 않게) */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {quote.isUnread && (
                <Badge
                  variant="outline"
                  className="border-blue-200 bg-blue-50 text-xs font-semibold text-blue-700"
                >
                  미열람
                </Badge>
              )}
              <StatusBadge status={quote.status} label={quote.statusLabel} />
              {/* meta는 배지 말고 텍스트로 아래로 내려도 됨 */}
            </div>

            {/* 하단: 메타 + 우측 정보 (모바일에선 같은 줄로 정리) */}
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                {quote.meta && <p className="truncate text-xs text-gray-600">{quote.meta}</p>}
              </div>

              <div className="shrink-0 text-right">
                <p className="text-xs text-gray-500">
                  제출일 · {quote.submittedAt ? formatDate(quote.submittedAt) : '-'}
                </p>
                <span className="text-xs font-semibold text-gray-500 group-hover:text-blue-700">
                  상세 보기 →
                </span>
              </div>
            </div>
          </Link>
        )
      })}
    </div>
  )
}
